<template>
  <div class="c-lookup-box" v-if="result && phrase && phrase.translation && term">
    <div class="c-lookup-box__content-basic">
      <div class="c-lookup-box__audio">
        <button
          class="c-lookup-box__button"
          :aria-label="`${$t('LISTEN_TO_PHRASE')}`"
          @click="playAudio()">
          <audio-icon class="c-lookup-box__audio-icon"/>
        </button>
        <audio
          ref="audio"
          hidden>
          <source
            :src="audioSrc"
            type="audio/mpeg"/>
        </audio>
        <span class="c-lookup-box__audio-text">{{ term }}</span>
      </div>
      <p
        v-if="phrase.baseForm"
        class="c-lookup-box__p c-lookup-box__base-form">
        {{ $t('BASE_FORM') }}: {{ phrase.baseForm }}</p>
      <p class="c-lookup-box__phrase">
        <strong class="c-lookup-box__strong c-lookup-box__strong--black">{{ phrase.translation }}</strong>
      </p>
      <p
        v-if="phrase.explanation"
        class="c-lookup-box__p c-lookup-box__explanation">
        {{ phrase.explanation }}
      </p>
    </div>
    <div
      v-if="showDetails || responsive === 'tablet'"
      class="c-lookup-box__content-detailed">
      <p
        v-if="phrase.wordClass"
        class="c-lookup-box__p c-lookup-box__detail">
        <strong class="c-lookup-box__strong">{{ $t('WORD_CLASS') }}:</strong> {{ phrase.wordClass }}
      </p>
      <p
        v-if="phrase.form"
        class="c-lookup-box__p c-lookup-box__detail">
        <strong class="c-lookup-box__strong">{{ $t('FORM') }}:</strong> {{ phrase.form }}</p>
      <p
        v-if="phrase.inflection && phrase.inflection.length"
        class="c-lookup-box__p c-lookup-box__detail">
        <strong class="c-lookup-box__strong">{{ $t('INFLECTION') }}: </strong>
        <span
          class="c-lookup-box__inflection"
          v-for="(inflection, index) in phrase.inflection"
          :key="inflection.id">
          {{ inflection.props.name }}<span v-if="index < (phrase.inflection.length - 1)">, </span>
        </span>
      </p>
      <p
        v-if="phrase.synonyms && phrase.synonyms.length"
        class="c-lookup-box__p c-lookup-box__detail">
        <strong class="c-lookup-box__strong">{{ $t('SYNONYMS') }}: </strong>
        <span
          class="c-lookup-box__synonyms"
          v-for="(synonym, index) in phrase.synonyms"
          :key="synonym.id">
          {{ synonym.props.name }}<span v-if="index < (phrase.synonyms.length - 1)">, </span>
        </span>
      </p>
      <p
        v-if="phrase.alternativeSpellings && phrase.alternativeSpellings.length"
        class="c-lookup-box__p c-lookup-box__detail">
        <strong class="c-lookup-box__strong">{{ $t('ALTERNATIVE_SPELLINGS') }}: </strong>
        <span
          class="c-lookup-box__alternative-spellings"
          v-for="(spelling, index) in phrase.alternativeSpellings"
          :key="spelling.id">
          {{ spelling.props.name }}<span v-if="index < (phrase.alternativeSpellings.length - 1)">, </span>
        </span>
      </p>
    </div>
    <button
      v-if="responsive === 'desktop'"
      class="c-lookup-box__button c-lookup-box__button--toggle"
      :aria-expanded="showDetails"
      @click="toggleDetails()">
      <arrow
        class="c-lookup-box__arrow"
        :class="showDetails ? 'c-lookup-box__arrow--top' : ''"/>
      <span
        v-if="!showDetails"
        class="c-lookup-box__toggle-text">
        {{ $t('VIEW_DETAILS') }}</span>
      <span
        v-else
        class="c-lookup-box__toggle-text">
        {{ $t('HIDE_DETAILS') }}</span>
    </button>
  </div>
</template>

<script>
import Arrow from '@/assets/svg/Arrow'
import AudioIcon from '@/assets/svg/AudioIcon'

export default {
  name: 'LookupBox',

  components: {
    'arrow': Arrow,
    'audio-icon': AudioIcon
  },

  props: {
    result: {
      type: Object,
      default: null
    },
    term: {
      type: String,
      default: ''
    },
    language: {
      type: String,
      default: 'nb_NO'
    },
    responsive: String
  },

  data() {
    return {
      phrase: null,
      showDetails: false
    }
  },

  computed: {
    audioSrc() {
      return this.result && this.phrase ? `${this.$root.$config.talkbook_url}/${this.language}/${this.term}` : ''
    }
  },

  watch: {
    result() {
      this.phrase = null
      this.prepareContent()
    },
  },

  mounted() {
    this.prepareContent()
  },

  methods: {
    playAudio() {
      this.$refs.audio.play()
    },

    toggleDetails() {
      this.showDetails = !this.showDetails
      this.$emit('toggle-show-details', this.showDetails)
      this.$emit('toggle-clicked')
    },

    prepareContent() {
      if (this.result) {
        let { phrase } = this.result

        this.phrase = {
          name: phrase.props ? phrase.props.name : null,
          baseForm: phrase.base_form ? phrase.base_form : null,
          explanation: phrase.props ? phrase.props.explanation : null,
          wordClass: phrase.wordclass ? phrase.wordclass.props.name : null,
          form: phrase.form ? phrase.form.props.name : null,
          inflection: phrase.inflections ? phrase.inflections : null,
          translation: this.result.translation ? this.result.translation.props.name : null,
          synonyms: phrase.synonyms ? phrase.synonyms : null,
          alternativeSpellings: phrase.alternative_spellings ? phrase.alternative_spellings : null,
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/_vars.scss';
@import '@/assets/scss/_mixin.scss';

.c-lookup-box {
  background-color: $white;
  padding: .8rem;
  margin-bottom: 1em;
  border-radius: .75em;
  text-align: left;
  color: $blue;
  text-transform: lowercase;
  font-size: 1rem;
}

.c-lookup-box__audio {
  line-height: 2.25rem;
  font-weight: bold;
  margin: 0 0 .5rem 0;

  .c-lookup-box__button {
    vertical-align: middle;
  }
}

.c-lookup-box__audio-text {
  vertical-align: middle;
  margin-left: .5rem;
}

.c-lookup-box__phrase {
  font-size: 2rem;
}

.c-lookup-box__inflection,
.c-lookup-box__synonyms,
.c-lookup-box__alternative-spellings {
  margin-right: .5rem;
}

.c-lookup-box__content-basic,
.c-lookup-box__content-detailed {
  color: $blue;
}

.c-lookup-box__p,
.c-lookup-box__strong {
  color: $blue;
  margin: .5rem 0;
}

.c-lookup-box__strong--black {
  color: $black;
}

.c-lookup-box__button {
  cursor: pointer;
  border: 0;
  background: transparent;
  color: $blue;
  font-size: 2rem;
  line-height: 1.5rem;
  font-weight: bold;

  &.c-lookup-box__button--toggle {
    display: block;
    text-transform: uppercase;
    font-size: .65rem;
    margin: .6rem 0 0 auto;
    line-height: 1rem;

    .c-lookup-box__toggle-text {
      display: inline-block;
      vertical-align: middle;
      text-decoration: underline;
      line-height: 1.5rem;
      letter-spacing: -0.22px;
    }

    &:hover {
      color: $lighter-dark-blue;
    }
  }

  .c-lookup-box__audio-icon:hover .a {
    fill: $lighter-dark-blue;
  }

  &:focus {
    @include a11yOutline();
  }
}

.c-lookup-box__arrow {
  display: inline-block;
  height: 1.75rem;
  width: 2rem;
  bottom: 0;
  margin: auto;
  vertical-align: bottom;

  .content {
    stroke: $orange!important;
  }

  &.c-lookup-box__arrow--top {
    transform: rotate(180deg);
    vertical-align: top;
  }
}
</style>
